import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, useI18n } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, Clock, Users, Diamond, CreditCard } from "lucide-react";

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function BookProduct() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const t = useT();
  const { locale } = useI18n();

  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [mode, setMode] = useState("credits");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!productId) return;
    api.get(`/coach/products/${productId}`)
      .then(({ data }) => {
        setProduct(data);
        if (data?.payment_modes?.length) setMode(data.payment_modes[0]);
      })
      .catch(() => setNotFound(true));
  }, [productId]);

  const fetchSlots = useCallback(async (d) => {
    if (!productId) return;
    setLoadingSlots(true); setSlot("");
    try {
      const { data } = await api.get(`/coach/products/${productId}/slots?date=${d}`);
      const list = Array.isArray(data?.slots) ? data.slots : [];
      const seen = new Set();
      setSlots(list.filter((sl) => (sl && !seen.has(sl)) ? (seen.add(sl), true) : false));
    } catch { setSlots([]); }
    finally { setLoadingSlots(false); }
  }, [productId]);

  useEffect(() => { fetchSlots(date); }, [date, fetchSlots]);

  const timeLabel = (iso) => {
    try { return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const confirm = async () => {
    if (!product) return;
    if (!slot) { toast.error(t("bkc.select_slot_first")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/coach/bookings/book", {
        product_id: product.id,
        start_at: slot,
        payment_mode: mode,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.open(data.checkout_url, "_blank");
        toast.success(t("bkc.checkout_opened"));
      } else {
        toast.success(t("bkc.booked"));
        await refreshUser();
        navigate("/app/bookings");
      }
    } catch (e) {
      toast.error(apiError(e, t("bkc.book_failed")));
    } finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <div className="space-y-4">
        <BackBtn t={t} navigate={navigate} />
        <p className="text-sm text-muted-foreground">{t("bkc.product_not_found")}</p>
      </div>
    );
  }
  if (!product) return <Spinner label={t("common.loading")} />;

  const canCredits = product.payment_modes?.includes("credits");
  const canStripe = product.payment_modes?.includes("stripe");

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="book-product-screen">
      <BackBtn t={t} navigate={navigate} />

      <div>
        <h1 className="font-display text-3xl text-foreground">{product.name}</h1>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>{t("bkc.with_coach", { name: product.coach_name })}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {product.duration_min} {t("bkc.min")}</span>
          {product.is_group ? <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {t("bkc.group")}</span> : null}
        </p>
      </div>

      {product.description ? (
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-sm leading-relaxed text-foreground">{product.description}</p>
        </div>
      ) : null}

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{t("bkc.pick_date")}</label>
        <Input
          type="date"
          value={date}
          min={todayISO()}
          onChange={(e) => setDate(e.target.value)}
          className="w-full rounded-xl sm:w-56"
          data-testid="book-date-input"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{t("bkc.slots")}</label>
        {loadingSlots ? (
          <Spinner className="min-h-[60px]" />
        ) : slots.length === 0 ? (
          <p className="text-sm italic text-muted-foreground" data-testid="book-no-slots">{t("bkc.no_slots")}</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="book-slot-grid">
            {slots.map((sl) => {
              const active = sl === slot;
              return (
                <button
                  key={sl}
                  data-testid={`slot-${sl}`}
                  onClick={() => setSlot(sl)}
                  className={`rounded-xl border px-4 py-2 text-sm transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/40 text-foreground hover:bg-accent/40"}`}
                >
                  {timeLabel(sl)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {canCredits && canStripe && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{t("bkc.payment")}</label>
          <div className="flex gap-2">
            <button
              data-testid="pay-credits"
              onClick={() => setMode("credits")}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm transition-colors ${mode === "credits" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/40 text-foreground hover:bg-accent/40"}`}
            >
              <Diamond className="h-4 w-4" /> {t("bkc.pay_credits", { n: product.price_credits })}
            </button>
            <button
              data-testid="pay-stripe"
              onClick={() => setMode("stripe")}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm transition-colors ${mode === "stripe" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/40 text-foreground hover:bg-accent/40"}`}
            >
              <CreditCard className="h-4 w-4" /> {eur(product.price_cents)}
            </button>
          </div>
        </div>
      )}

      <Button
        className="w-full rounded-full"
        size="lg"
        disabled={busy || !slot}
        onClick={confirm}
        data-testid="confirm-booking"
      >
        {busy ? t("common.saving") : (mode === "credits"
          ? t("bkc.confirm_credits", { n: product.price_credits })
          : t("bkc.confirm_card", { amount: eur(product.price_cents) }))}
      </Button>
    </div>
  );
}

function BackBtn({ t, navigate }) {
  return (
    <button onClick={() => navigate(-1)} data-testid="book-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ChevronLeft className="h-4 w-4" /> {t("bkc.back")}
    </button>
  );
}
